/**
 * Home.
 *
 * Shows the shape of today without spoiling it: the bench colours are visible
 * (you will see them immediately anyway), but the target is not.
 */

import { h, ICON, svg } from '../dom';
import { ctx } from '../ctx';
import { dateForDayIndex, longDate, puzzleNumber, todayIndex } from '../../core/schedule';
import { GRADES } from '../../game/grade';
import { recent } from '../../state/store';
import { swatch } from '../paint';
import { panelSheet } from '../chrome';
import { helpSheet } from './help';
import { statsSheet } from './stats';
import { unlock } from '../audio';

export function homeScreen(): HTMLElement {
  const { stats, puzzle } = ctx;
  const today = todayIndex();
  const played = ctx.result;

  const screen = h('div', { class: 'screen home' });

  // --- Bar -----------------------------------------------------------------
  screen.appendChild(
    h(
      'div',
      { class: 'bar' },
      h('div', { class: 'bar__spacer' }),
      stats.streak > 0 &&
        h(
          'div',
          { class: 'streak', data: { hot: stats.streak >= 3 } },
          h('span', { class: 'streak__flame', text: '🔥' }),
          h('span', { text: `${stats.streak}` }),
          h('span', { class: 'caps', text: 'day streak' }),
        ),
      h(
        'button',
        {
          class: 'icon',
          attrs: { type: 'button', 'aria-label': 'How to play' },
          on: { click: () => { unlock(); panelSheet('How to play', helpSheet); } },
        },
        svg(ICON.help),
      ),
    ),
  );

  // --- Scroll --------------------------------------------------------------
  const scroll = h('div', { class: 'scroll' });

  scroll.appendChild(
    h(
      'div',
      { class: 'brand' },
      h('div', { class: 'brand__mark', text: 'DRIFT' }),
      h('div', { class: 'brand__tag', text: 'Mix the day’s colour' }),
    ),
  );

  if (!puzzle) return screen;

  // --- Today's card --------------------------------------------------------
  const card = h('div', {
    class: 'today',
    style: { '--peek-glow': 'rgba(255,255,255,0.06)' },
  });

  card.appendChild(
    h(
      'div',
      { class: 'today__head' },
      h(
        'div',
        {},
        h('div', { class: 'today__n', text: `Puzzle #${puzzleNumber(ctx.index)}` }),
        h('div', { class: 'today__date', text: longDate(ctx.index) }),
      ),
      h('div', { class: 'today__badge', text: puzzle.spec.label }),
    ),
  );

  card.appendChild(
    h('div', { class: 'today__blurb', text: puzzle.spec.blurb }),
  );

  card.appendChild(
    h(
      'div',
      { class: 'today__specs' },
      spec(String(puzzle.spec.pigments), 'pigments'),
      spec(String(puzzle.spec.blends), 'blends'),
      spec(String((puzzle.spec.pigments * (puzzle.spec.pigments - 1)) / 2), 'first pairs'),
    ),
  );

  // Bench preview. A spoiler-free look at the palette you will be given.
  card.appendChild(
    h(
      'div',
      { class: 'peek' },
      ...puzzle.bench.map((p) =>
        h('div', { class: 'peek__chip', style: swatch(p), attrs: { 'aria-hidden': 'true' } }),
      ),
    ),
  );

  scroll.appendChild(card);
  scroll.appendChild(h('div', { class: 'rule', style: { margin: 'var(--s-5) 0' } }));

  // --- This week -----------------------------------------------------------
  scroll.appendChild(
    h('div', { class: 'section__head' },
      h('div', { class: 'caps', text: 'This week' }),
      h('button', {
        class: 'link',
        text: 'Archive →',
        attrs: { type: 'button' },
        on: { click: () => { unlock(); ctx.go('archive'); } },
      }),
    ),
  );
  scroll.appendChild(historyStrip(today));
  scroll.appendChild(h('div', { style: { height: 'var(--s-5)' } }));

  screen.appendChild(scroll);

  // --- Foot ----------------------------------------------------------------
  const foot = h('div', { class: 'home__foot' });

  foot.appendChild(
    h(
      'button',
      {
        class: 'home__stats',
        attrs: { type: 'button', 'aria-label': 'Statistics' },
        on: { click: () => { unlock(); panelSheet('Statistics', statsSheet); } },
      },
      tile(String(stats.maxStreak), 'best', stats.maxStreak >= 3),
      tile(stats.played ? `${Math.round((stats.wins / stats.played) * 100)}%` : '—', 'win rate'),
      tile(String(stats.played), 'played'),
    ),
  );

  const label = played
    ? `Played · ${GRADES[played.grade].label}`
    : ctx.mode === 'archive'
      ? 'Replay'
      : 'Play today';

  foot.appendChild(
    h('button', {
      class: `btn btn--wide btn--lg ${played ? '' : 'btn--primary'}`,
      attrs: { type: 'button' },
      text: label,
      on: {
        click: () => {
          unlock();
          if (played) ctx.go('reveal');
          else ctx.start(ctx.index, 'daily');
        },
      },
    }),
  );

  screen.appendChild(foot);
  return screen;
}

function spec(value: string, key: string): HTMLElement {
  return h(
    'div',
    { class: 'spec' },
    h('div', { class: 'spec__n', text: value }),
    h('div', { class: 'spec__k', text: key }),
  );
}

function tile(value: string, key: string, gold = false): HTMLElement {
  return h(
    'div',
    { class: 'stat', data: { accent: gold ? 'gold' : undefined } },
    h('div', { class: 'stat__n', text: value }),
    h('div', { class: 'stat__k', text: key }),
  );
}

/** Seven tiles: the week so far, as colours you actually mixed. */
function historyStrip(today: number): HTMLElement {
  const strip = h('div', { class: 'strip' });
  const days = recent(7, today);

  for (const day of days.slice().reverse()) {
    const index = day.index;
    const result = 'grade' in day ? day : null;
    const isToday = index === today;
    const date = dateForDayIndex(index);

    strip.appendChild(
      h(
        'div',
        { class: 'strip__day' },
        h('div', {
          class: 'strip__tile',
          style: result ? swatch(result.hex) : undefined,
          data: { state: result ? 'done' : isToday ? 'today' : 'empty' },
          text: result ? GRADES[result.grade].symbol : isToday ? '·' : '',
          attrs: { 'aria-hidden': 'true' },
        }),
        h('div', {
          class: 'strip__k',
          text: date.toLocaleDateString(undefined, { weekday: 'narrow' }),
        }),
      ),
    );
  }

  return strip;
}

export { statsSheet, helpSheet };
